import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from '../../store/hooks';
import { getProjectImage } from '../../Module/Project/Presentation/projectActions';
import { getCloudFrontImageUrl, CONTENT_IMAGE_DIMENSIONS } from '../../Utils/cloudFrontImage';
import { normalizeStoryHtmlForDisplay } from '../../Utils/projectDesc';

interface SafeHtmlViewerProps {
  html: string;
  className?: string;
  postData?: any;
}

const SafeHtmlViewer: React.FC<SafeHtmlViewerProps> = ({ html, className, postData }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  // 슬롯 플레이스홀더를 실제 이미지로 치환 + HTML 내 mfimage URL을 CloudFront로 변환
  const processedHtml = React.useMemo(() => {
    if (!html) return html;

    let result = normalizeStoryHtmlForDisplay(html);
    if (postData) {
      for (let i = 2; i <= 5; i++) {
        const placeholder = `[[IMG_SLOT_${i}]]`;
        const url = postData[`image${i}_url` as keyof typeof postData];

        if (result.includes(placeholder)) {
          if (url) {
            const cloudFrontUrl = getCloudFrontImageUrl(url, CONTENT_IMAGE_DIMENSIONS);
            const replacement = `<figure data-embed="story-image"><img src="${cloudFrontUrl}" data-embed="image" width="100%"></figure>`;
            result = result.replace(new RegExp(`\\[\\[IMG_SLOT_${i}\\]\\]`, 'g'), replacement);
          } else {
            result = result.replace(new RegExp(`\\[\\[IMG_SLOT_${i}\\]\\]`, 'g'), '');
          }
        }
      }
    }

    // HTML 내 mfimage, genesis, 기타 OMF 이미지 URL을 CloudFront 축소 URL로 치환
    result = result.replace(
      /(src=")((?:https?:\/\/[^"]*\/OMF\/[^"]+|\/OMF\/[^"]+))(")/g,
      (_, prefix, url, suffix) => `${prefix}${getCloudFrontImageUrl(url, CONTENT_IMAGE_DIMENSIONS)}${suffix}`,
    );
    return result;
  }, [html, postData]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resolveImages = async () => {
      const images = containerRef.current?.querySelectorAll('img[data-embed="image"]');
      if (!images) return;

      images.forEach(async (img) => {
        const imageElement = img as HTMLImageElement;
        const src = imageElement.getAttribute('src');

        // URL 형태인지 확인 (http, https, data:, blob:)
        if (!src || src.startsWith('http') || src.startsWith('data:') || src.startsWith('blob:')) {
            return;
        }

        // src가 ID인 경우 (URL 형식이 아님)
        try {
          const result = await dispatch(getProjectImage({ image_id: src })).unwrap();
          if (result.image_url) {
            imageElement.src = getCloudFrontImageUrl(result.image_url, CONTENT_IMAGE_DIMENSIONS);
          }
        } catch (error) {
          console.error('Failed to resolve embedded image:', error);
        }
      });
    };

    resolveImages();
  }, [processedHtml, dispatch]);

  return (
    <Container
      ref={containerRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  );
};

const Container = styled.div`
  word-break: break-word;

  img {
    max-width: 100%;
    height: auto;
  }

  figure[data-embed='story-image'] {
    margin: 1em 0;
    max-width: 100%;

    img {
      max-width: 100%;
      height: auto;
      display: block;
    }
  }
`;

export default SafeHtmlViewer;
